/**
 * Tiny imperative snackbar provider.
 *
 * Usage:
 *   const snack = useSnackbar();
 *   snack.success('Tenant created');
 *   snack.error('Could not save');
 */
import {
  Alert,
  Snackbar as MuiSnackbar,
  type AlertColor,
} from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface SnackbarMessage {
  id: number;
  severity: AlertColor;
  text: string;
}

interface SnackbarApi {
  success(text: string): void;
  info(text: string): void;
  warning(text: string): void;
  error(text: string): void;
}

const SnackbarContext = createContext<SnackbarApi | null>(null);

let nextId = 1;

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<SnackbarMessage[]>([]);

  const push = useCallback((severity: AlertColor, text: string) => {
    setQueue((q) => [...q, { id: nextId++, severity, text }]);
  }, []);

  const api = useMemo<SnackbarApi>(
    () => ({
      success: (text) => push('success', text),
      info: (text) => push('info', text),
      warning: (text) => push('warning', text),
      error: (text) => push('error', text),
    }),
    [push],
  );

  const current = queue[0];
  const handleClose = () => setQueue((q) => q.slice(1));

  return (
    <SnackbarContext.Provider value={api}>
      {children}
      {current ? (
        <MuiSnackbar
          open
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          key={current.id}
        >
          <Alert
            severity={current.severity}
            onClose={handleClose}
            variant="filled"
            sx={{ minWidth: 280 }}
          >
            {current.text}
          </Alert>
        </MuiSnackbar>
      ) : null}
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarApi {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within a SnackbarProvider');
  return ctx;
}
